import {
  IssueCaseDoc,
  IssueCaseStatusCode,
  IssueTaskCategory,
  IssueTaskDoc,
  IssueTaskStatus,
} from '@gooduncles/gu-app-schema';
import { chunk } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { combineLatest, of, switchMap } from 'rxjs';
import { IssueCaseAnyCategory, ISSUE_CASE_ACTIVE_STATUSES } from 'src/constants/issue.constants';

import { formatDate } from 'src/utils/date-util';
import { observeIssueCases, observeIssueTasks } from 'src/utils/firebase/firebase-short-cut';
import { processIssueData } from 'src/utils/issue/issue-data-util';

import { IssueManageMasterRowData } from '../../components/Issue/IssueManage/IssueManage.types';

export interface RankingItem {
  id: string;
  name: string;
  count: number;
}

export interface IssueStoreSummary {
  total: number;
  byStatus: Record<IssueCaseStatusCode, number>;
  byCategory: Record<IssueCaseAnyCategory, number>;
  topProducts: RankingItem[];
  taskTotal: number;
  taskByStatus: Record<IssueTaskStatus, number>;
  taskByCategory: Record<IssueTaskCategory, number>;
}

const calculateStoreSummary = (cases: IssueCaseDoc[], tasks: IssueTaskDoc[]): IssueStoreSummary => {
  // Case 통계
  const byStatus = cases.reduce(
    (acc, c) => {
      acc[c.status] = (acc[c.status] || 0) + 1;
      return acc;
    },
    {} as Record<IssueCaseStatusCode, number>,
  );

  const byCategory = cases.reduce(
    (acc, c) => {
      acc[c.category] = (acc[c.category] || 0) + 1;
      return acc;
    },
    {} as Record<IssueCaseAnyCategory, number>,
  );

  // 상품별 이슈 집계
  const productCounts = new Map<string, { name: string; count: number }>();
  cases.forEach((c) => {
    c.products?.forEach((p) => {
      const existing = productCounts.get(p.productId);
      if (existing) {
        existing.count += 1;
      } else {
        productCounts.set(p.productId, { name: p.productName, count: 1 });
      }
    });
  });

  const topProducts: RankingItem[] = Array.from(productCounts.entries())
    .map(([id, { name, count }]) => ({ id, name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // Task 통계
  const taskByStatus = tasks.reduce(
    (acc, t) => {
      acc[t.status] = (acc[t.status] || 0) + 1;
      return acc;
    },
    {} as Record<IssueTaskStatus, number>,
  );

  const taskByCategory = tasks.reduce(
    (acc, t) => {
      acc[t.category] = (acc[t.category] || 0) + 1;
      return acc;
    },
    {} as Record<IssueTaskCategory, number>,
  );

  return {
    total: cases.length,
    byStatus,
    byCategory,
    topProducts,
    taskTotal: tasks.length,
    taskByStatus,
    taskByCategory,
  };
};

interface UseIssueStoreManagementDataOptions {
  storeId: string | null;
  startDate: string;
  endDate: string;
}

export const useIssueStoreManagementData = ({ storeId, startDate, endDate }: UseIssueStoreManagementDataOptions) => {
  const [rowData, setRowData] = useState<IssueManageMasterRowData[]>([]);
  const [allCases, setAllCases] = useState<IssueCaseDoc[]>([]);
  const [allTasks, setAllTasks] = useState<IssueTaskDoc[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!storeId || !startDate || !endDate) {
      setRowData([]);
      setAllCases([]);
      setAllTasks([]);
      return;
    }

    setIsLoading(true);

    const formattedStartDate = formatDate(new Date(startDate), "yyyy-MM-dd'T'00:00:00+0900");
    const formattedEndDate = formatDate(new Date(endDate), "yyyy-MM-dd'T'23:59:59+0900");

    const casesObservable = observeIssueCases(
      [
        ['storeId', '==', storeId],
        ['registeredAt', '>=', formattedStartDate],
        ['registeredAt', '<=', formattedEndDate],
      ],
      {
        sortKey: 'registeredAt',
        orderBy: 'desc',
      },
    );

    // issueCase 조회 후 해당 caseId들로 issueTask 조회
    const subscription = casesObservable
      .pipe(
        switchMap((cases) => {
          const filteredCases = cases.filter((issueCase) => ISSUE_CASE_ACTIVE_STATUSES.includes(issueCase.status));
          const caseIds = filteredCases.map((c) => c._id);

          if (caseIds.length === 0) {
            return of({ cases: filteredCases, tasks: [] as IssueTaskDoc[] });
          }

          // Firestore "in" 쿼리는 최대 30개 제한이므로 chunk로 나눔
          const caseIdChunks = chunk(caseIds, 30);
          const taskObservables = caseIdChunks.map((ids) => observeIssueTasks([['issueCaseId', 'in', ids]]));

          return combineLatest(taskObservables).pipe(
            switchMap((taskArrays) => {
              const allTasks = taskArrays.flat();
              return of({ cases: filteredCases, tasks: allTasks });
            }),
          );
        }),
      )
      .subscribe({
        next: ({ cases, tasks }) => {
          setAllCases(cases);
          setAllTasks(tasks);
          const processedData = processIssueData(cases, tasks);
          setRowData(processedData);
          setIsLoading(false);
        },
        error: (err) => {
          console.error('Failed to fetch issues', err);
          setIsLoading(false);
        },
      });

    return () => subscription.unsubscribe();
  }, [storeId, startDate, endDate]);

  const summary = useMemo(() => calculateStoreSummary(allCases, allTasks), [allCases, allTasks]);

  return {
    rowData,
    summary,
    isLoading,
  };
};
